import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import type { Locale } from '../../types/locale';

interface WatermarkDemoCardProps {
  heading: string;
  headingLabel: string;
  sampleText: {
    before: React.ReactNode;
    after: string;
  };
  detectedCount: number;
  detectedLabel: string;
  cleanedLabel: string;
  stats: {
    stat1: { value: string; label: string };
    stat2: { value: string; label: string };
    stat3: { value: string; label: string };
  };
  // Locale for accessibility translations
  locale?: Locale;
}

/**
 * Localized accessibility labels for WatermarkDemoCard
 * SEO-optimized with natural phrasing per locale
 */
const translations: Record<Locale, {
  logoAlt: string;
  beforeLabel: string;
  afterLabel: string;
  transformArrowLabel: string;
}> = {
  en: {
    logoAlt: 'GPT Watermark Remover logo',
    beforeLabel: 'Original text with watermarks',
    afterLabel: 'Clean text without watermarks',
    transformArrowLabel: 'Transformation arrow showing before to after',
  },
  de: {
    logoAlt: 'GPT Wasserzeichen Entferner Logo',
    beforeLabel: 'Originaltext mit Wasserzeichen',
    afterLabel: 'Bereinigter Text ohne Wasserzeichen',
    transformArrowLabel: 'Transformationspfeil von vorher zu nachher',
  },
  'pt-BR': {
    logoAlt: 'Logo do Removedor de Marca d\'Agua GPT',
    beforeLabel: 'Texto original com marcas d\'agua',
    afterLabel: 'Texto limpo sem marcas d\'agua',
    transformArrowLabel: 'Seta de transformacao mostrando antes e depois',
  },
  'ms-MY': {
    logoAlt: 'Logo Pembuang Tera Air GPT',
    beforeLabel: 'Teks asal dengan tera air',
    afterLabel: 'Teks bersih tanpa tera air',
    transformArrowLabel: 'Anak panah transformasi menunjukkan sebelum ke selepas',
  },
  es: {
    logoAlt: 'Logo del Eliminador de Marcas de Agua GPT',
    beforeLabel: 'Texto original con marcas de agua',
    afterLabel: 'Texto limpio sin marcas de agua',
    transformArrowLabel: 'Flecha de transformacion mostrando antes y despues',
  },
  fr: {
    logoAlt: 'Logo du Suppresseur de Filigrane GPT',
    beforeLabel: 'Texte original avec filigranes',
    afterLabel: 'Texte nettoye sans filigranes',
    transformArrowLabel: 'Fleche de transformation montrant avant et apres',
  },
  ja: {
    logoAlt: 'GPT透かし除去ツールのロゴ',
    beforeLabel: '透かし付きの元のテキスト',
    afterLabel: '透かしのないクリーンなテキスト',
    transformArrowLabel: 'ビフォーアフターを示す変換矢印',
  },
  id: {
    logoAlt: 'Logo Penghapus Watermark GPT',
    beforeLabel: 'Teks asli dengan watermark',
    afterLabel: 'Teks bersih tanpa watermark',
    transformArrowLabel: 'Panah transformasi menunjukkan sebelum dan sesudah',
  },
};

/**
 * WatermarkDemoCard - Visual before/after demo for pSEO pages
 *
 * CRITICAL: All content must be 100% unique per page to avoid duplicate content.
 * Only the visual structure/styling is shared - all text, numbers, and labels are unique.
 */
export const WatermarkDemoCard: React.FC<WatermarkDemoCardProps> = ({
  heading,
  headingLabel,
  sampleText,
  detectedCount,
  detectedLabel,
  cleanedLabel,
  stats,
  locale = 'en',
}) => {
  const t = translations[locale] || translations.en;

  return (
    <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-800 p-8">
      <div className="relative space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="relative p-3 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center border border-accent-100 dark:border-accent-800">
            <img
              src="/favicon-96x96.webp"
              alt={t.logoAlt}
              width="32"
              height="32"
              loading="lazy"
              decoding="async"
              className="w-8 h-8"
            />
          </div>
          <div>
            <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              {headingLabel}
            </div>
            <div className="text-2xl font-bold text-neutral-900 dark:text-white">
              {detectedCount} {heading}
            </div>
          </div>
        </div>

        {/* Sample Text with Highlights - UNIQUE per page */}
        <div className="space-y-4">
          <div
            className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700"
            aria-label={t.beforeLabel}
          >
            <div className="text-sm text-neutral-600 dark:text-neutral-300 font-mono leading-relaxed">
              {sampleText.before}
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <div
              className="p-2 bg-neutral-100 dark:bg-neutral-800 rounded-full text-neutral-600 dark:text-neutral-400"
              aria-label={t.transformArrowLabel}
            >
              <ArrowRight size={20} className="rotate-90" aria-hidden="true" />
            </div>
          </div>

          {/* Clean Text */}
          <div
            className="p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-900/30"
            aria-label={t.afterLabel}
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle
                size={16}
                className="text-green-600 dark:text-green-500"
                aria-hidden="true"
              />
              <span className="text-xs font-semibold text-green-700 dark:text-green-400">
                {cleanedLabel}
              </span>
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300 font-mono leading-relaxed">
              {sampleText.after}
            </div>
          </div>
        </div>

        {/* Stats - UNIQUE per page */}
        <div className="grid grid-cols-3 gap-4 pt-6 border-t border-neutral-100 dark:border-neutral-800">
          <div className="text-center">
            <div className="text-xl font-bold text-neutral-900 dark:text-white">
              {stats.stat1.value}
            </div>
            <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mt-1">
              {stats.stat1.label}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-neutral-900 dark:text-white">
              {stats.stat2.value}
            </div>
            <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mt-1">
              {stats.stat2.label}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-neutral-900 dark:text-white">
              {stats.stat3.value}
            </div>
            <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mt-1">
              {stats.stat3.label}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Helper component for highlighted watermark text
 * Use this to show where watermarks appear in the "before" text
 */
export const WatermarkHighlight: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 px-1 mx-0.5 rounded border border-yellow-200 dark:border-yellow-800">
    {children || '\u200B'}
  </span>
);

export default WatermarkDemoCard;
